import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import Cookies from 'js-cookie'
import * as authApi from '@/api/auth'
import * as userApi from '@/api/user'
const API_URL = process.env.VUE_APP_API_URL || 'https://www.echosphere.ltd'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()

  // 状态
  const user = ref(null)
  const accessToken = ref(Cookies.get('access_token') || null)
  const refreshToken = ref(Cookies.get('refresh_token') || null)
  const permissions = ref([])
  const isLoading = ref(false)

  // 计算属性
  const isAuthenticated = computed(() => !!accessToken.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isManager = computed(() => user.value?.role === 'manager')
  const userInitials = computed(() => {
    if (!user.value) return ''
    const name = user.value.full_name || user.value.username
    return name.charAt(0).toUpperCase()
  })

  // 方法
  const setTokens = (access, refresh) => {
    accessToken.value = access
    refreshToken.value = refresh

    if (access) {
      Cookies.set('access_token', access, { expires: 1 })
    } else {
      Cookies.remove('access_token')
    }

    if (refresh) {
      Cookies.set('refresh_token', refresh, { expires: 30 })
    } else {
      Cookies.remove('refresh_token')
    }
  }

  const setUser = (userData) => {
    user.value = userData
    // 存储用户权限
    if (userData?.permissions) {
      permissions.value = userData.permissions
    }
  }

  const clearAuth = () => {
    user.value = null
    accessToken.value = null
    refreshToken.value = null
    permissions.value = []

    Cookies.remove('access_token')
    Cookies.remove('refresh_token')
    Cookies.remove('user_info')
  }

  const login = async (credentials) => {
    try {
      isLoading.value = true
      const response = await authApi.login(credentials)

      setTokens(response.access_token, response.refresh_token)
      setUser(response.user)

      ElMessage.success('登录成功')
      return Promise.resolve(response)
    } catch (error) {
      const message = error.response?.data?.error || '登录失败'
      ElMessage.error(message)
      return Promise.reject(error)
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch (error) {
      console.error('登出失败:', error)
    } finally {
      clearAuth()
      await router.push('/auth/login')
      ElMessage.success('已退出登录')
    }
  }

const fetchProfile = async () => {
  if (!accessToken.value) {
    return Promise.reject(new Error('未登录'));
  }

  try {
    const response = await fetch(`${API_URL}/work/auth/profile`, {
      headers: {
        'Authorization': `Bearer ${accessToken.value}`
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearAuth();
        throw new Error('认证已过期');
      }
      throw new Error('获取用户信息失败');
    }

    const data = await response.json();
    setUser(data.user);

    return Promise.resolve(data);
  } catch (error) {
    // 如果获取失败但本地有缓存用户，使用缓存
    const savedUser = localStorage.getItem('user_info');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        console.log('使用缓存的用户信息');
      } catch (e) {
        console.error('恢复用户信息失败:', e);
      }
    }
    return Promise.reject(error);
  }
}

  const refreshAccessToken = async () => {
    try {
      if (!refreshToken.value) {
        throw new Error('没有刷新令牌')
      }

      const response = await authApi.refresh(refreshToken.value)
      setTokens(response.access_token, refreshToken.value)

      // 重新获取用户信息
      await fetchProfile()

      return Promise.resolve(response)
    } catch (error) {
      clearAuth()
      return Promise.reject(error)
    }
  }

  const updateProfile = async (data) => {
    try {
      const response = await userApi.updateUser(user.value.id, data)
      setUser(response.user)
      ElMessage.success('资料更新成功')
      return Promise.resolve(response)
    } catch (error) {
      ElMessage.error(error.response?.data?.error || '更新失败')
      return Promise.reject(error)
    }
  }

  const changePassword = async (data) => {
    try {
      await userApi.changePassword(user.value.id, data)
      ElMessage.success('密码修改成功')
      return Promise.resolve()
    } catch (error) {
      ElMessage.error(error.response?.data?.error || '修改失败')
      return Promise.reject(error)
    }
  }

  const checkPermission = async (environment, tenantVersion) => {
    try {
      const response = await authApi.checkPermission(environment, tenantVersion)
      return Promise.resolve(response)
    } catch (error) {
      return Promise.reject(error)
    }
  }

  const hasDataScope = (environment, tenantVersion) => {
    if (isAdmin.value) return true
    if (!user.value?.data_scopes) return false

    return user.value.data_scopes.some(scope =>
      scope.environment === environment &&
      scope.tenant_version === tenantVersion
    )
  }

  // 初始化
const init = async () => {
  // 从 localStorage 恢复用户信息
  const savedUser = localStorage.getItem('user_info');
  if (savedUser) {
    try {
      setUser(JSON.parse(savedUser));
    } catch (e) {
      console.error('恢复用户信息失败:', e);
    }
  }

  // 如果存在 token，尝试获取最新用户信息
  if (accessToken.value) {
    try {
      await fetchProfile();
    } catch (error) {
      console.error('初始化用户信息失败:', error);
      // 如果 token 无效，清除认证状态
      if (error.message === '获取用户信息失败' && error.response?.status === 401) {
        clearAuth();
      }
    }
  }
}

  // 返回
  return {
    // 状态
    user,
    accessToken,
    refreshToken,
    permissions,
    isLoading,

    // 计算属性
    isAuthenticated,
    isAdmin,
    isManager,
    userInitials,

    // 方法
    setTokens,
    setUser,
    clearAuth,
    login,
    logout,
    fetchProfile,
    refreshAccessToken,
    updateProfile,
    changePassword,
    checkPermission,
    hasDataScope,
    init
  }
})